import React, { useEffect, useState } from "react"
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated"
import { Pressable, View } from "react-native"
import DealArchWithText from "@/assets/clipart/dial-arch-with-text.svg"
import NewDealPointer from "@/components/analysis/NewDealPointer"
import { abbreviateCurrency, numberToCurrency } from "@/utils/NumberUtils"
import Tooltip from "rn-tooltip"
import { BFColors } from "@/constants/Colors"
import InfoIcon from "@/assets/icons/tooltip-i-notfilled.svg"
import { type AnalysisType } from "@/generated/graphql"
import { useEditAnalysis } from "@/queries/analysis"
import useCompsData from "@/hooks/useCompsData"
import { useStore } from "@nanostores/react"
import { currentStrategy, setStrategy } from "@/stores/strategyStore"
import { type AnalyzerStrategy } from "@/types/analysisTypes"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { NoCompsAvailableColumn } from "@/components/analysis/NoCompsInfo"
import BFText from "@/components/standard/BFText"
import tooltips from "@/constants/ToolTips"
import { ARVMeetExpectationsQuestionPopup } from "@/screens/analyze/components/ARVMeetExpectationsQuestionPopup"
import { ARGiveFeedbackButton } from "@/screens/analyze/components/ARGiveFeedbackButton"
import {
  analyzePropertyARVFeedbackClicked,
  analyzePropertyGiveARVFeedbackClicked,
  lowCompsTooltipClicked,
} from "@/analytics/events/analyzer"
import { showSuccessToast } from "@/components/toasts/AppToast"
import {
  markARVQuestionedShowed,
  showARVQuestion,
} from "@/stores/arvQuestionsAsked"
import CompsAlert from "@/assets/icons/comps-alert.svg"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"
import { emptyPlaceholder } from "@/components/analysis/MarketData"
import { SecondaryLightButton } from "@/components/buttons/CommonButtons"

interface DealMeterProps {
  title?: string
  activeTab: "Comps" | "Calculate"
  analysis: AnalysisType | null
  onEnterArv?: () => void
}

const DealMeterBlock: React.FC<DealMeterProps> = ({
  title = "NET PROFIT POTENTIAL",
  activeTab,
  analysis,
  onEnterArv,
}) => {
  const [anchorViewRef, setAnchorViewRef] = useState<View | null>(null)
  const [showARVFeedbackPopup, setShowARVFeedbackPopup] = useState(false)
  const { showBackflipSuggestedComps } = useAnalyzeContext()
  const { animatedDimStyle } = useUpdateDimming()
  const rotationMin: number = -18
  const rotationMax: number = 182
  const rotation = useSharedValue(rotationMin)
  const returnOnInvestment: number = analysis?.returnOnInvestment || 0

  useEffect(() => {
    if (!analysis?.id) {
      return
    }

    if (showARVQuestion(analysis.id)) {
      setShowARVFeedbackPopup(true)
      markARVQuestionedShowed(analysis.id)
    }
  }, [analysis?.id])

  useEffect(() => {
    let angle
    if (returnOnInvestment <= 0) {
      angle = rotationMin
    } else if (returnOnInvestment >= 20) {
      angle = rotationMax
    } else {
      // Linear interpolation between rotationMin and rotationMax based on returnOnInvestment
      const range = rotationMax - rotationMin
      const normalizedROI = returnOnInvestment / 20 // Normalize ROI to a 0-1 scale
      angle = rotationMin + range * normalizedROI
    }

    rotation.value = withSpring(angle, {
      damping: 10,
      stiffness: 10,
      mass: 1.1,
    })
  }, [returnOnInvestment, rotation])

  const hasComps: boolean =
    (analysis?.comps && analysis?.comps.length > 0) || false

  const profitText = (): string => {
    if (!hasComps || !showBackflipSuggestedComps) {
      return showBackflipSuggestedComps ? "---" : "$0"
    }
    if (analysis?.profitPotential) {
      return abbreviateCurrency(analysis?.profitPotential) || "---"
    }
    return "---"
  }

  const monthlyCashText = (): string => {
    if (!hasComps || !showBackflipSuggestedComps) {
      return showBackflipSuggestedComps ? "---" : "$0"
    }
    if (analysis?.monthlyCashFlow) {
      return abbreviateCurrency(analysis?.monthlyCashFlow) || "---"
    }
    return "---"
  }

  const animatedStyle = useAnimatedStyle(() => {
    return {
      position: "absolute",
      marginTop: 42,
      transform: [{ rotate: `${rotation.value?.toString() || "0"}deg` }],
    }
  })

  if (!analysis) {
    console.log("* NO Analysis for Dial -")
    return <></>
  }

  return (
    <>
      <View className="w-full flex-row min-h-[163px]">
        <View className="flex-1 items-center py-6">
          {hasComps ? (
            activeTab === "Comps" ? (
              <ARVBlockComps
                popupRef={setAnchorViewRef}
                key={analysis.strategy}
                analysis={analysis}
              />
            ) : (
              <AnalysisModeBlock key={analysis.strategy} analysis={analysis} />
            )
          ) : (
            <NoCompsAvailableColumn onEnterArv={onEnterArv || (() => {})} />
          )}
        </View>

        <View className="w-px bg-neutral-300 h-full" />

        <View className="flex-1 items-center py-6">
          <View className="flex-1 items-center">
            {/* Vertical tool line */}

            <View className="flex-row items-start">
              <BFText className="h-[28px] text-center text-gray-500 text-[11px] font-normal font-spacegrotesk leading-[12px] mr-1">
                {analysis.strategy === "Fix & Flip" ? title : "CASH (mo)"}
              </BFText>
              <Tooltip
                popover={
                  <BFText
                    style={{ lineHeight: 15 }}
                    className="font-spacegrotesk text-white text-[12px] p-[2px]"
                  >
                    {analysis.strategy === "Fix & Flip"
                      ? tooltips.netProfitPotentialGraph
                      : tooltips.rentPotential}
                  </BFText>
                }
                actionType="press"
                backgroundColor={BFColors.calmBlue}
                withOverlay={false}
                toggleWrapperProps={{
                  hitSlop: 16,
                }}
                height={"auto"}
                width={290}
                containerStyle={{
                  borderRadius: 2,
                }}
              >
                <View style={{ marginTop: -3 }}>
                  <InfoIcon width={15} height={15} />
                </View>
              </Tooltip>
            </View>

            <Pressable
              testID={"graph-dial-area"}
              className="relative w-[142px] h-[86px]"
              hitSlop={8}
              onPress={onEnterArv}
            >
              <DealArchWithText />
              <Animated.View style={animatedStyle}>
                <NewDealPointer show={hasComps} />
              </Animated.View>
              <Animated.View
                className="absolute w-full bottom-[12px] text-center text-black text-[18px] font-figtree-extrabold leading-tight"
                style={animatedDimStyle}
              >
                <BFText className="text-center text-black text-[18px] font-figtree-extrabold leading-tight">
                  {analysis.strategy === "Fix & Flip"
                    ? profitText()
                    : monthlyCashText()}
                </BFText>
              </Animated.View>
            </Pressable>
          </View>
        </View>
      </View>

      {anchorViewRef && showBackflipSuggestedComps && (
        <ARGiveFeedbackButton
          onPress={() => {
            analyzePropertyGiveARVFeedbackClicked()
            setShowARVFeedbackPopup(true)
            markARVQuestionedShowed(analysis.id)
          }}
        />
      )}

      {showARVFeedbackPopup && anchorViewRef && showBackflipSuggestedComps && (
        <ARVMeetExpectationsQuestionPopup
          onClose={(feedback?: string) => {
            setShowARVFeedbackPopup(false)
            if (feedback) {
              analyzePropertyARVFeedbackClicked(feedback, analysis)
              if ((analysis?.comps?.length || 0) < 3) {
                showSuccessToast({
                  title: "Thanks!",
                  message:
                    "Did you know you can get more customized comps by filtering your results?",
                  autoHide: true,
                })
              } else {
                showSuccessToast({
                  title: "Thanks!",
                  message:
                    "Did you know the estimated ARV changes when you update your selected comps?",
                  autoHide: true,
                })
              }
            }
          }}
          anchor={anchorViewRef}
        />
      )}
    </>
  )
}

export default DealMeterBlock

interface ARVBlockCompsProps {
  analysis: AnalysisType
  popupRef: (ref: View | null) => void
}

const ARVBlockComps: React.FC<ARVBlockCompsProps> = ({
  analysis,
  popupRef,
}) => {
  const { animatedDimStyle } = useUpdateDimming()
  const { isFirstManualAnalysisDisplay } = useAnalyzeContext()

  const { selectedCompCount, totalCompCount, averagePricePerSqft } =
    useCompsData(analysis.property)

  return (
    <View className="flex-1 size-full flex-col items-center">
      <View ref={popupRef}>
        <BFText className="w-[111px] h-[15px] text-center text-gray-500 text-[10px] font-normal font-spacegrotesk leading-3">
          ESTIMATED ARV
        </BFText>
        <Animated.View style={animatedDimStyle}>
          <BFText className="text-center text-midnight-blue text-[24px] font-figtree-extrabold leading-7">
            {isFirstManualAnalysisDisplay
              ? "$"
              : `${numberToCurrency(analysis?.afterRepairValue || "---")}`}
          </BFText>
        </Animated.View>
      </View>

      {/* Horz tool line */}
      <View className="mt-2 ml-6 self-stretch h-px bg-neutral-300" />

      <View className="flex-row my-4 gap-10">
        <View className="justify-center items-center">
          <BFText className="h-[28px] text-center text-gray-500 text-[11px] font-spacegrotesk leading-none">
            COMPS{"\n"}SELECTED
          </BFText>

          <Animated.View style={animatedDimStyle}>
            <View className="flex-row">
              <BFText className="justify-center text-center text-slate-800 font-figtree-extrabold leading-tight tracking-tight">
                {isFirstManualAnalysisDisplay
                  ? emptyPlaceholder
                  : `${selectedCompCount} of ${totalCompCount}`}
              </BFText>
              {selectedCompCount < 3 && (
                <Tooltip
                  onOpen={() => {
                    lowCompsTooltipClicked(analysis)
                  }}
                  popover={
                    <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
                      {tooltips.threeOrMoreComps}
                    </BFText>
                  }
                  actionType="press"
                  backgroundColor={BFColors.calmBlue}
                  withOverlay={false}
                  height={"auto"}
                  width={250}
                  containerStyle={{
                    borderRadius: 2,
                  }}
                  toggleWrapperProps={{
                    hitSlop: 32,
                  }}
                >
                  <View className="pl-[2px] pt-[3px]">
                    <CompsAlert />
                  </View>
                </Tooltip>
              )}
            </View>
          </Animated.View>
        </View>

        <View className="justify-center items-center">
          <BFText className="h-[28px] text-center text-gray-500 text-[11px] font-normal font-spacegrotesk leading-none">
            AVG $/{"\n"}SQ FT
          </BFText>
          <Animated.View style={animatedDimStyle}>
            <BFText className="justify-center text-center text-slate-800 font-figtree-extrabold leading-tight tracking-tight">
              {isFirstManualAnalysisDisplay
                ? emptyPlaceholder
                : `${numberToCurrency(Math.round(averagePricePerSqft))}`}
            </BFText>
          </Animated.View>
        </View>
      </View>
    </View>
  )
}

interface AnalysisModeBlockProps {
  analysis: AnalysisType
}

const AnalysisModeBlock: React.FC<AnalysisModeBlockProps> = ({ analysis }) => {
  const strategy = useStore(currentStrategy)

  const editAnalysisMutation = useEditAnalysis()

  useEffect(() => {
    if (
      (analysis.strategy as AnalyzerStrategy) &&
      analysis.strategy !== strategy
    ) {
      setStrategy(analysis.strategy as AnalyzerStrategy)
    }
  }, [analysis.strategy])

  const onFixAndFlip = () => {
    setStrategy("Fix & Flip")
    editAnalysisMutation.mutate({
      analysisId: analysis.id,
      analysisInput: { strategy: "Fix & Flip" },
    })
  }

  const onRehabAndRent = () => {
    setStrategy("Rehab & Rent")
    editAnalysisMutation.mutate({
      analysisId: analysis.id,
      analysisInput: { strategy: "Rehab & Rent" },
    })
  }

  return (
    <View className="flex-1 size-full items-center justify-center">
      <View className="flex-col gap-4 mb-6">
        <SecondaryLightButton
          testID={"flip-button"}
          title="FIX/FLIP"
          size="sm"
          className={"w-[120px]"}
          onPress={onFixAndFlip}
          selected={strategy === "Fix & Flip"}
        />

        <SecondaryLightButton
          testID={"rent-button"}
          title="REHAB/RENT"
          size="sm"
          className={"w-[120px]"}
          onPress={onRehabAndRent}
          selected={strategy === "Rehab & Rent"}
        />
      </View>
    </View>
  )
}
